import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';

import { EMPTY_ARRAY } from '@e-socna/empty';

import { useFormikOnNextTick } from '../../../../../hooks/useFormikOnNextTick.hook';
import { useAuthenticationContext } from '../../../../../contexts/AuthenticationContext/hooks/useAuthenticationContext.hook';
import { useReferencesContext } from '../../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { newRepresentativeOf } from '../../../utils/new-representative.util';
import type { ActorForm_Actor } from '../../../fragments/actor.fragment';

// The representatives belong to the chosen organization: choosing another
// third party resets them to the connected user when they are an active member
// of it, else empties them, while the actor's own ones are kept when it is
// opened for modification.
export const useRepresentativesReset = () => {
  const {
    values: { thirdPartyId },
  } = useFormikContext<ActorForm_Actor>();
  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();
  const { authenticationData } = useAuthenticationContext();
  const { genericStatusesCodeMap } = useReferencesContext();
  const previousThirdPartyIdRef = useRef(thirdPartyId);

  const connectedPerson = authenticationData?.person;
  const enabledStatusId = genericStatusesCodeMap.Enabled._id;

  useEffect(() => {
    if (thirdPartyId === previousThirdPartyIdRef.current) {
      return;
    }

    previousThirdPartyIdRef.current = thirdPartyId;

    const isConnectedPersonMember = !!connectedPerson?.allMemberships.some(
      ({ organizationId, statusId }) =>
        organizationId === thirdPartyId && statusId === enabledStatusId,
    );

    setFieldValueOnNextTick(
      'representatives',
      isConnectedPersonMember && connectedPerson
        ? [newRepresentativeOf(connectedPerson._id, 0)]
        : EMPTY_ARRAY,
    );
  }, [connectedPerson, enabledStatusId, thirdPartyId, setFieldValueOnNextTick]);
};
